/**
 * @fileoverview Scout Report Parser
 * @description Turns a Scout "consolidated scores" HTML report into a compact record
 * that can be stored, rendered, and compared. Only the rollup data is kept — the
 * multi-megabyte row narratives in the embedded detail reports are intentionally dropped.
 * @module features/ScoutEval/utils/scoutReportParser
 */

const SCORE_CLASS_BANDS = {
  'score-high': 'green',
  'score-mid': 'amber',
  'score-low': 'red',
  'score-na': 'not-run',
};

const VERDICT_TONES = {
  'verdict-good': 'green',
  'verdict-watch': 'amber',
  'verdict-bad': 'red',
  'verdict-na': 'not-run',
};

/** Scores at or above this value meet the monitoring target. */
const BELOW_TARGET_THRESHOLD = 0.75;

/** Scores at or above this value are on target. */
const ON_TARGET_THRESHOLD = 0.85;

/**
 * Collapse whitespace of a node's text.
 * @param {Element|null} node - Source node.
 * @returns {string} Normalized text.
 */
function text(node) {
  return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
}

/**
 * Parse a numeric score, tolerating labels such as "NONE" or "n/a".
 * @param {string} raw - Raw cell text.
 * @returns {number|null} Score or null when not numeric.
 */
function toScore(raw) {
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : null;
}

/**
 * Resolve the score band for a numeric score.
 * @param {number|null} score - Normalized score between 0 and 1.
 * @returns {string} Band key.
 */
export function bandForScore(score) {
  if (score === null || score === undefined) {
    return 'not-run';
  }
  if (score >= ON_TARGET_THRESHOLD) {
    return 'green';
  }
  if (score >= BELOW_TARGET_THRESHOLD) {
    return 'amber';
  }
  return 'red';
}

/**
 * Prefer the band the report already rendered, falling back to the score thresholds.
 * Non-numeric values (for example "NONE") are always treated as unscored. A report that
 * bands a rounded boundary score such as 0.75 as below target is corrected here so the
 * published thresholds always hold.
 * @param {Element|null} badge - Score badge element.
 * @param {number|null} score - Parsed score.
 * @returns {string} Band key.
 */
function bandFromBadge(badge, score) {
  if (score === null) {
    return 'not-run';
  }
  const classes = badge ? Array.from(badge.classList) : [];
  const match = classes.find((name) => SCORE_CLASS_BANDS[name]);
  const reported = match ? SCORE_CLASS_BANDS[match] : null;

  if (!reported || (reported === 'red' && score >= BELOW_TARGET_THRESHOLD)) {
    return bandForScore(score);
  }
  return reported;
}

/**
 * Strip a leading "Label:" prefix from a meta chip.
 * @param {string} value - Chip text.
 * @returns {string} Value without its label.
 */
function withoutLabel(value) {
  return value.replace(/^[^:]+:\s*/, '');
}

/**
 * Read the header meta chips of a report.
 * @param {Document} doc - Parsed document.
 * @returns {{generatedAt: string, sources: Array<{name: string, rows: number|null}>, chips: string[]}} Meta data.
 */
function parseHeaderMeta(doc) {
  const chips = Array.from(doc.querySelectorAll('header .meta span')).map(text).filter(Boolean);
  const generated = chips.find((chip) => /^generated/i.test(chip));
  const sourceChip = chips.find((chip) => /^sources/i.test(chip));

  const sources = sourceChip
    ? withoutLabel(sourceChip).split('·').map((entry) => {
      const [name, detail = ''] = entry.split(/:\s*/);
      const rows = Number.parseInt(detail, 10);
      return { name: name.trim(), rows: Number.isFinite(rows) ? rows : null };
    }).filter((source) => source.name)
    : [];

  return {
    generatedAt: generated ? withoutLabel(generated) : '',
    sources,
    chips: chips.filter((chip) => !/^sources/i.test(chip)),
  };
}

/**
 * Read a `.metrics` strip into label/score/note triples.
 * @param {ParentNode} root - Element containing metric cards.
 * @returns {Array<{label: string, value: string, score: number|null, band: string, note: string}>} Metrics.
 */
function parseMetricCards(root) {
  return Array.from(root.querySelectorAll('.metric-card')).map((card) => {
    const value = text(card.querySelector('strong'));
    const score = toScore(value);
    return {
      label: text(card.querySelector('.metric-label')),
      value,
      score,
      band: bandFromBadge(card.querySelector('strong'), score),
      note: text(card.querySelector('.metric-note')),
    };
  });
}

/**
 * Locate a metric card by a label pattern.
 * @param {Array<Object>} metrics - Parsed metric cards.
 * @param {RegExp} pattern - Label matcher.
 * @returns {Object|undefined} Matching metric.
 */
function findMetric(metrics, pattern) {
  return metrics.find((metric) => pattern.test(metric.label));
}

/**
 * Build the stable identity of an evaluator across runs.
 * @param {string} field - Report field.
 * @param {string} evaluator - Evaluator name.
 * @returns {string} Comparison key.
 */
export function evaluatorKey(field, evaluator) {
  return `${field}||${evaluator}`.toLowerCase();
}

/**
 * Read the consolidated summary table.
 * @param {Document} doc - Parsed document.
 * @returns {Array<Object>} Evaluator rows.
 */
function parseEvaluatorRows(doc) {
  const rows = Array.from(doc.querySelectorAll('#summaryTable tbody tr'));

  return rows.map((row, position) => {
    const cells = Array.from(row.querySelectorAll('td'));
    const badge = row.querySelector('.score-badge');
    const score = toScore(text(badge));
    const field = text(cells[1]);
    const evaluator = text(cells[2]);

    return {
      key: evaluatorKey(field, evaluator),
      index: text(cells[0]) || String(position + 1),
      field,
      evaluator,
      framework: text(cells[3]),
      approach: text(cells[4]),
      score,
      band: bandFromBadge(badge, score),
      detailId: row.querySelector('[data-panel-target]')?.getAttribute('data-panel-target') || '',
      search: (row.getAttribute('data-search') || `${field} ${evaluator}`).toLowerCase(),
    };
  });
}

/**
 * Read the collapsible source text of an event cell.
 * @param {Element} cell - Event cell.
 * @returns {Array<{heading: string, body: string}>} Source sections.
 */
function parseSourceSections(cell) {
  const container = cell.querySelector('details');
  if (!container) {
    return [];
  }

  const sections = [];
  let heading = '';
  Array.from(container.children).forEach((node) => {
    if (node.tagName === 'H4') {
      heading = text(node);
    } else if (node.tagName === 'P') {
      sections.push({ heading, body: node.textContent.trim() });
    }
  });
  return sections;
}

/**
 * Read the rationale and evidence paragraphs of a dimension cell.
 * @param {Element} cell - Dimension cell.
 * @returns {Array<{label: string, body: string}>} Labelled notes.
 */
function parseRationale(cell) {
  const block = cell.querySelector('.rationale-block');
  if (!block) {
    return [];
  }

  const notes = [];
  let label = '';
  Array.from(block.children).forEach((node) => {
    if (node.tagName === 'STRONG') {
      label = text(node);
    } else if (node.tagName === 'P') {
      notes.push({ label, body: node.textContent.trim() });
    }
  });
  return notes;
}

/**
 * Read one scored dimension of an event row.
 * @param {Element} cell - Dimension cell.
 * @param {string} column - Column heading.
 * @returns {Object} Dimension result.
 */
function parseDimensionCell(cell, column) {
  const badge = cell.querySelector('.score-badge');
  const verdict = cell.querySelector('.verdict-badge');
  const value = text(badge);
  const score = toScore(value);
  const verdictClass = verdict
    ? Array.from(verdict.classList).find((name) => VERDICT_TONES[name])
    : null;

  return {
    column,
    label: text(cell.querySelector('.label-text')),
    value,
    score,
    band: bandFromBadge(badge, score),
    verdict: text(verdict),
    verdictTone: verdictClass ? VERDICT_TONES[verdictClass] : 'not-run',
    suggested: withoutLabel(text(cell.querySelector('.suggested'))),
    notes: parseRationale(cell),
  };
}

/**
 * Read the row-level evaluation table used by the group reports.
 * @param {Document} doc - Detail report document.
 * @returns {Object|null} Row layout, or null when the report has no event table.
 */
function parseEventTable(doc) {
  const table = doc.getElementById('reportTable');
  if (!table) {
    return null;
  }

  const columns = Array.from(table.querySelectorAll('thead th')).map(text);

  return {
    layout: 'rows',
    columns,
    searchPlaceholder: doc.getElementById('searchBox')?.getAttribute('placeholder') || 'Search detail rows',
    verdictOptions: Array.from(doc.querySelectorAll('#verdictFilter option'))
      .map((option) => ({ value: option.value, label: text(option) })),
    rows: Array.from(table.querySelectorAll('tbody tr')).map((row, position) => {
      const cells = Array.from(row.querySelectorAll('td'));
      const eventCell = cells[0];

      return {
        key: `row-${position}`,
        eventId: text(eventCell?.querySelector('.event-number')),
        title: text(eventCell?.querySelector('.event-title')),
        sourceId: text(eventCell?.querySelector('.source-id')),
        source: eventCell ? parseSourceSections(eventCell) : [],
        verdicts: (row.getAttribute('data-verdict') || '').split(/\s+/).filter(Boolean),
        cells: cells.slice(1).map((cell, index) => parseDimensionCell(cell, columns[index + 1] || '')),
      };
    }),
  };
}

/**
 * Read a plain table into headings and typed cells.
 * @param {Element} table - Table element.
 * @returns {{columns: string[], rows: Array<Array<Object>>}} Table content.
 */
function parseSimpleTable(table) {
  return {
    columns: Array.from(table.querySelectorAll('thead th')).map(text),
    rows: Array.from(table.querySelectorAll('tbody tr'))
      .filter((row) => !row.classList.contains('blank'))
      .map((row) => Array.from(row.querySelectorAll('td')).map((cell) => {
        const pill = cell.querySelector('.score-pill, .score-badge');
        const value = pill ? text(pill) : cell.textContent.trim();
        const score = pill ? toScore(value) : null;
        return { value, score, band: pill ? bandFromBadge(pill, score) : null };
      })),
  };
}

/**
 * Read the label/value pairs of a card meta grid.
 * @param {Element} card - Card element.
 * @returns {Array<{label: string, value: string}>} Meta pairs.
 */
function parseMetaGrid(card) {
  const grid = card.querySelector('.meta-grid');
  if (!grid) {
    return [];
  }

  const nodes = Array.from(grid.children);
  return nodes.reduce((pairs, node, index) => (
    node.classList.contains('meta-label') && nodes[index + 1]
      ? [...pairs, { label: text(node), value: text(nodes[index + 1]) }]
      : pairs
  ), []);
}

/**
 * Read the card layout used by the title and questions report.
 * @param {Document} doc - Detail report document.
 * @returns {Object|null} Card layout, or null when the report has no cards.
 */
function parseCardLayout(doc) {
  const cards = Array.from(doc.querySelectorAll('.card')).map((card, position) => {
    const table = card.querySelector('table');
    const kpiValue = text(card.querySelector('.kpi-pill'));

    return {
      key: `card-${position}`,
      heading: text(card.querySelector('h1, h2')) || text(card.querySelector('.kpi-label')),
      subhead: text(card.querySelector('.subhead')),
      meta: parseMetaGrid(card),
      kpi: kpiValue ? { label: text(card.querySelector('.kpi-label')), value: kpiValue } : null,
      highlights: Array.from(card.querySelectorAll('.summary-panel li')).map(text).filter(Boolean),
      collapsedLabel: text(card.querySelector('details > summary')),
      table: table ? parseSimpleTable(table) : null,
      note: text(card.querySelector('.note')),
    };
  }).filter((card) => card.heading || card.table || card.highlights.length || card.meta.length);

  return cards.length ? { layout: 'cards', cards } : null;
}

/**
 * Read an embedded detail report. Handles both the metric-strip layout used by the
 * group reports and the card layout used by the title/questions report. The rollup is
 * returned alongside the full row-level content so nothing in the report is dropped.
 * @param {string} html - Detail report markup.
 * @returns {Object} Normalized detail report.
 */
function parseDetailReport(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const metricStrip = parseMetricCards(doc);

  const resultsTable = Array.from(doc.querySelectorAll('.card')).find(
    (card) => /evaluation results/i.test(text(card.querySelector('h2'))),
  );
  const tableMetrics = resultsTable
    ? Array.from(resultsTable.querySelectorAll('tbody tr'))
      .filter((row) => !row.classList.contains('blank'))
      .map((row) => {
        const cells = Array.from(row.querySelectorAll('td'));
        const badge = row.querySelector('.score-pill');
        const value = text(badge) || text(cells[1]);
        const score = toScore(value);
        return {
          label: text(cells[0]),
          value,
          score,
          band: bandFromBadge(badge, score),
          note: text(cells[2]),
        };
      })
      .filter((metric) => metric.label)
    : [];

  const kpi = Number.parseInt(text(doc.querySelector('.kpi-pill')), 10);
  const tableRowCount = doc.querySelectorAll('#reportTable tbody tr').length;

  return {
    title: text(doc.querySelector('header h1')) || text(doc.querySelector('.card h1')) || text(doc.querySelector('.brand-title')),
    description: text(doc.querySelector('header p')) || text(doc.querySelector('.subhead')),
    chips: Array.from(doc.querySelectorAll('header .meta span')).map(text).filter(Boolean),
    metrics: metricStrip.length ? metricStrip : tableMetrics,
    highlights: Array.from(doc.querySelectorAll('.summary-panel li')).map(text).filter(Boolean),
    rowCount: tableRowCount || (Number.isFinite(kpi) ? kpi : null),
    content: parseEventTable(doc) || parseCardLayout(doc),
  };
}

/**
 * Read every detail tab and its embedded report.
 * @param {Document} doc - Parsed document.
 * @returns {Array<Object>} Detail groups.
 */
function parseGroups(doc) {
  return Array.from(doc.querySelectorAll('.report-tab[data-panel-target]'))
    .map((tab) => ({ id: tab.getAttribute('data-panel-target'), label: text(tab) }))
    .filter((tab) => tab.id && tab.id !== 'summary-panel')
    .map((tab) => {
      const srcdoc = doc.getElementById(tab.id)?.querySelector('iframe')?.getAttribute('srcdoc');
      return { ...tab, ...(srcdoc ? parseDetailReport(srcdoc) : { metrics: [], highlights: [], chips: [], rowCount: null }) };
    });
}

/**
 * Count evaluators per score band.
 * @param {Array<Object>} evaluators - Evaluator rows.
 * @returns {{green: number, amber: number, red: number, notRun: number}} Distribution.
 */
export function bandDistribution(evaluators) {
  return (evaluators || []).reduce((counts, row) => {
    const bucket = row.band === 'not-run' ? 'notRun' : row.band;
    return bucket in counts ? { ...counts, [bucket]: counts[bucket] + 1 } : counts;
  }, { green: 0, amber: 0, red: 0, notRun: 0 });
}

/**
 * Parse a Scout consolidated evaluation report.
 * @param {string} html - Raw report markup.
 * @param {string} fileName - Uploaded file name.
 * @returns {Object} Stored run record.
 * @throws {Error} When the file is not a Scout consolidated report.
 */
export function parseScoutReport(html, fileName) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const evaluators = parseEvaluatorRows(doc);

  if (evaluators.length === 0) {
    throw new Error('This file does not look like a Scout consolidated report — no evaluator summary table was found.');
  }

  const meta = parseHeaderMeta(doc);
  const summaryPanel = doc.getElementById('summary-panel') || doc;
  const metrics = parseMetricCards(summaryPanel);
  const scored = evaluators.filter((row) => row.score !== null);
  const distribution = bandDistribution(evaluators);

  const overallCard = findMetric(metrics, /overall average/i);
  const scoredCard = findMetric(metrics, /evaluators scored/i);
  const notRun = Number.parseInt(scoredCard?.note, 10);

  const overallAverage = overallCard?.score ?? (scored.length
    ? scored.reduce((sum, row) => sum + row.score, 0) / scored.length
    : 0);

  return {
    id: `scout-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`,
    fileName,
    importedAt: new Date().toISOString(),
    workspace: text(doc.querySelector('header .eyebrow')) || 'Scout AI Evaluation',
    title: text(doc.querySelector('header h1')) || 'AI Evaluators – Consolidated Scores',
    description: text(doc.querySelector('header p')),
    generatedAt: meta.generatedAt,
    metaChips: meta.chips,
    sources: meta.sources,
    summary: {
      overallAverage,
      overallBand: bandForScore(overallAverage),
      evaluatorsScored: Number.parseInt(scoredCard?.value, 10) || scored.length,
      notRun: Number.isFinite(notRun) ? notRun : evaluators.length - scored.length,
      belowTarget: distribution.red,
      totalRows: meta.sources.reduce((sum, source) => sum + (source.rows || 0), 0),
      ...distribution,
    },
    evaluators,
    groups: parseGroups(doc),
  };
}

/**
 * Split a parsed run into the lightweight record kept in local storage and the row-level
 * detail content, which is far too large for local storage and is kept separately.
 * @param {Object} run - Parsed run.
 * @returns {{record: Object, details: Object}} Storage split.
 */
export function splitRunContent(run) {
  const details = {};
  const groups = run.groups.map(({ content, ...rollup }) => {
    if (content) {
      details[rollup.id] = content;
    }
    return rollup;
  });

  return { record: { ...run, groups }, details };
}

export default parseScoutReport;
